import { FC } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useSelector } from "react-redux";
import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";
import { tApplicationBrandPath } from "src/redux/Application/types";
import { tReduxState } from "src/redux/types";
import { aBrandCategories, aBrandStoreCount, aPriceRange, aStates, hearAboutUsOptions } from "src/util/comboBoxData";
import { normalizeStateName } from "src/util/gFormatting";
import {
  FormComboBoxInput,
  TextFormInput,
  tValidType,
  ValidatedComboBoxInput,
  ValidatedTextFormInput,
  ValidatedYesNoButtons,
} from "./FormInputs";

const stateNormalizer = (val: string) => normalizeStateName(val) || "State";
const addressSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.address.address1;
const addressTwoSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.address.address2;
const cityNameSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.address.city;
const taxIdSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.taxId;
const websiteSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.website;
const noteSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.note;
const facebookSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.socialMedia?.facebook;
const instagramSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.socialMedia?.instagram;
const stateSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.address.state_or_province;
const postalCodeSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.address.postal_code;
const retailerInUsSelector = ({ ApplicationReducer }: tReduxState) =>
  ApplicationReducer.type === "brand" ? ApplicationReducer.independentRetailersInUs : false;
const retailerQuantitySelector = ({ ApplicationReducer }: tReduxState) =>
  ApplicationReducer.type === "brand" ? ApplicationReducer.independentRetailerQuantity : "none";
const canConductBusinessSelector = ({ ApplicationReducer }: tReduxState) =>
  ApplicationReducer.type === "brand" ? ApplicationReducer.canConductBusinessInUs : undefined;
const isBasedInUSSelector = ({ ApplicationReducer }: tReduxState) =>
  ApplicationReducer.type === "brand" && ApplicationReducer.isBasedInUs;
const nameSelector = ({ ApplicationReducer }: tReduxState) =>
  ApplicationReducer.type === "brand" ? ApplicationReducer.name : "";
const brandCategorySelector = ({ ApplicationReducer }: tReduxState) =>
  ApplicationReducer.type === "brand" ? ApplicationReducer.category : undefined;
const pricePointSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.price;
const hearAboutUsSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.hearAboutUs;
const _isShown = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.isShown;

export const BrandApplication: FC<{
  isValid: tValidType;
  setValid: React.Dispatch<React.SetStateAction<tValidType>>;
}> = ({ isValid, setValid }) => {
  const rIsBasedInUS = useSelector(
    ({ ApplicationReducer }) => ApplicationReducer.type === "brand" && ApplicationReducer.isBasedInUs,
  );
  const rRetailerInUs = useSelector(
    ({ ApplicationReducer }) => ApplicationReducer.type === "brand" && ApplicationReducer.independentRetailersInUs,
  );
  const rHearAboutUsDisabled = useSelector(({ ApplicationReducer }) => ApplicationReducer.hearAboutUsDisabled);

  return (
    <>
      <View style={styles.gApplyTablePageContainer}>
        <View style={styles.gFormContainer}>
          <Text style={styles.sectionTitle}>Company Information</Text>
          <ValidatedTextFormInput<tApplicationBrandPath>
            wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
            titleViewStyle={styles.rowNoMargin}
            titleProps={{ style: styles.P08_01_InputHeader }}
            controller={isValid}
            myTitle="Company Name *"
            uniqueName="companyName"
            rules={{ required: { message: "Required" } }}
            textInputProps={{
              style: styles.gLinkBankInput,
              placeholder: "Company Name",
              autoCompleteType: "organization",
            }}
            dispatchAction={{ type: "UPDATE_BRAND_APPLICATION", applicationPart: { name: "" } }}
            dispatchPath="applicationPart.name"
            setController={setValid}
            selectorFunction={nameSelector}
            isShown={_isShown}
          />
          <ValidatedTextFormInput<tApplicationBrandPath>
            wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
            titleViewStyle={styles.rowNoMargin}
            titleProps={{ style: styles.P08_01_InputHeader }}
            controller={isValid}
            rules={{ required: { message: "Required" } }}
            myTitle="Address *"
            uniqueName="address1"
            textInputProps={{
              style: styles.gLinkBankInput,
              placeholder: "1111 Street name",
              autoCompleteType: "address-line1",
            }}
            dispatchAction={{
              type: "UPDATE_BRAND_APPLICATION",
              applicationPart: { address: { address1: "" } },
            }}
            dispatchPath="applicationPart.address.address1"
            setController={setValid}
            selectorFunction={addressSelector}
            isShown={_isShown}
          />
          <TextFormInput<tApplicationBrandPath>
            wrapperStyle={styles.gFormRowNoMarginSingle}
            titleStyle={styles.P08_01_InputHeader}
            myTitle="Address Line 2"
            textInputProps={{
              placeholder: "E.g. Apt. 1",
              autoCompleteType: "address-line2",
              style: styles.gLinkBankInput,
            }}
            dispatchAction={{
              type: "UPDATE_BRAND_APPLICATION",
              applicationPart: { address: { address2: "" } },
            }}
            dispatchPath="applicationPart.address.address2"
            selectorFunction={addressTwoSelector}
          />
          <View style={styles.gFormRowNoMarginZIndex}>
            <ValidatedTextFormInput<tApplicationBrandPath>
              wrapperStyle={styles.gLinkBankFormItem}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              myTitle="City *"
              uniqueName="cityName"
              rules={{ required: { message: "Required" } }}
              controller={isValid}
              textInputProps={{
                style: styles.gLinkBankTripleInput,
                placeholder: "City Name",
              }}
              dispatchAction={{
                type: "UPDATE_BRAND_APPLICATION",
                applicationPart: { address: { city: "" } },
              }}
              dispatchPath="applicationPart.address.city"
              selectorFunction={cityNameSelector}
              setController={setValid}
              isShown={_isShown}
            />
            <ValidatedComboBoxInput<tApplicationBrandPath>
              wrapperStyle={[styles.gLinkBankFormItemZIndex, { marginBottom: kStyleConst.kSpacing }]}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              controller={isValid}
              ComboOptions={aStates}
              rules={{ required: { message: "Required" } }}
              uniqueName="State"
              myTitle="State *"
              placeholder="State"
              normalizerFunction={stateNormalizer}
              autoComplete="address-level1"
              isShown={_isShown}
              setController={setValid}
              dispatchAction={{
                type: "UPDATE_BRAND_APPLICATION",
                applicationPart: { address: { state_or_province: "" } },
              }}
              dispatchPath="applicationPart.address.state_or_province"
              selectorFunction={stateSelector}
            />
            {/* <ValidatedComboBoxInput
          wrapperStyle={styles.gLinkBankFormItemZIndex}
          controller={sIsValid}
          ComboOptions={aCountries}
          rules={{ required: { message: "Required" } }}
          uniqueName="country"
          myTitle="Country *"
          placeholder="Country"
          normalizerFunction={countryNormalizer}
          autoComplete="country-name"
          titleProps={{ style: styles.P08_01_InputHeader }}
          isShown={_isShown}
          setController={setValid}
          onSelectChange={_countryOptionsChange}
          selectorFunction={countrySelector}
        /> */}
            <ValidatedTextFormInput<tApplicationBrandPath>
              wrapperStyle={styles.gLinkBankFormItemBottomMargin}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              controller={isValid}
              uniqueName="ZipCode"
              myTitle="Zip Code *"
              rules={{
                required: { message: "Required" },
                pattern: { pattern: /\d{5}/, message: "Enter 5 digit zipcode" },
              }}
              textInputProps={{
                placeholder: "12345",
                style: styles.gLinkBankTripleInput,
                maxLength: 5,
                autoCompleteType: "postal-code",
              }}
              dispatchAction={{
                type: "UPDATE_BRAND_APPLICATION",
                applicationPart: { address: { postal_code: "" } },
              }}
              dispatchPath="applicationPart.address.postal_code"
              setController={setValid}
              isShown={_isShown}
              selectorFunction={postalCodeSelector}
            />
          </View>
          <View style={styles.gFormRowNoMargin}>
            <TextFormInput<tApplicationBrandPath>
              wrapperStyle={styles.gLinkBankFormItem}
              titleStyle={styles.P08_01_InputHeader}
              myTitle="Tax ID #"
              textInputProps={{
                style: styles.gLinkBankInput,
                placeholder: "Company Tax Id",
              }}
              dispatchAction={{
                type: "UPDATE_BRAND_APPLICATION",
                applicationPart: { taxId: "" },
              }}
              dispatchPath="applicationPart.taxId"
              selectorFunction={taxIdSelector}
            />
            <ValidatedTextFormInput<tApplicationBrandPath>
              wrapperStyle={styles.gLinkBankFormItemNoMargin}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              myTitle="Website *"
              uniqueName="website"
              rules={{
                required: { message: "Required" },
              }}
              controller={isValid}
              textInputProps={{
                style: styles.gLinkBankInput,
                placeholder: "Company Website",
                autoCompleteType: "url",
              }}
              dispatchAction={{
                type: "UPDATE_BRAND_APPLICATION",
                applicationPart: { website: "" },
              }}
              dispatchPath="applicationPart.website"
              setController={setValid}
              selectorFunction={websiteSelector}
              isShown={_isShown}
            />
          </View>
          <View style={styles.gFormRowNoMargin}>
            <ValidatedTextFormInput<tApplicationBrandPath>
              wrapperStyle={styles.gLinkBankFormItem}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              controller={isValid}
              myTitle="Facebook"
              uniqueName="facebook"
              textInputProps={{
                placeholder: "www.facebook.com/storename",
                style: styles.gLinkBankInput,
              }}
              dispatchAction={{
                type: "UPDATE_BRAND_APPLICATION",
                applicationPart: { socialMedia: { facebook: "" } },
              }}
              dispatchPath="applicationPart.socialMedia.facebook"
              setController={setValid}
              selectorFunction={facebookSelector}
              isShown={_isShown}
            />
            <ValidatedTextFormInput<tApplicationBrandPath>
              wrapperStyle={styles.gLinkBankFormItemNoMargin}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              myTitle="Instagram"
              uniqueName="instagram"
              controller={isValid}
              textInputProps={{
                placeholder: "www.instagram.com/storename",
                style: styles.gLinkBankInput,
              }}
              dispatchAction={{
                type: "UPDATE_BRAND_APPLICATION",
                applicationPart: { socialMedia: { instagram: "" } },
              }}
              dispatchPath="applicationPart.socialMedia.instagram"
              selectorFunction={instagramSelector}
              setController={setValid}
              isShown={_isShown}
            />
          </View>
          <ValidatedYesNoButtons
            wrapperStyle={styles.gLinkBankFormItemYesNo}
            titleViewStyle={styles.P08_01_InputHeaderWrap}
            titleProps={{ style: styles.P08_01_InputHeader }}
            myTitle="Is this company based in the United States? *"
            uniqueName="usBased"
            controller={isValid}
            rules={{ required: { message: "Required" } }}
            setController={setValid}
            isShown={_isShown}
            dispatchActionTrue={{
              type: "UPDATE_BRAND_APPLICATION",
              applicationPart: { isBasedInUs: true, canConductBusinessInUs: true },
            }}
            dispatchActionFalse={{
              type: "UPDATE_BRAND_APPLICATION",
              applicationPart: { isBasedInUs: false, canConductBusinessInUs: undefined },
            }}
            selectorFunction={isBasedInUSSelector}
          />
          {rIsBasedInUS === false && (
            <ValidatedYesNoButtons
              myTitle="Are you able to conduct business in the US? *"
              wrapperStyle={styles.gLinkBankFormItemYesNo}
              titleViewStyle={styles.P08_01_InputHeaderWrap}
              titleProps={{ style: styles.P08_01_InputHeader }}
              controller={isValid}
              uniqueName="operateUS"
              rules={{ required: { message: "Required" } }}
              setController={setValid}
              isShown={_isShown}
              dispatchActionTrue={{
                type: "UPDATE_BRAND_APPLICATION",
                applicationPart: { canConductBusinessInUs: true },
              }}
              dispatchActionFalse={{
                type: "UPDATE_BRAND_APPLICATION",
                applicationPart: { canConductBusinessInUs: false },
              }}
              selectorFunction={canConductBusinessSelector}
            />
          )}
          <ValidatedYesNoButtons
            myTitle="Do you currently have U.S. Retail Distribution in Independent Retailers? *"
            wrapperStyle={styles.gLinkBankFormItemYesNo}
            titleViewStyle={styles.P08_01_InputHeaderWrap}
            titleProps={{ style: styles.P08_01_InputHeader }}
            controller={isValid}
            uniqueName="distributeUS"
            rules={{ required: { message: "Required" } }}
            setController={setValid}
            isShown={_isShown}
            dispatchActionTrue={{
              type: "UPDATE_BRAND_APPLICATION",
              applicationPart: { independentRetailersInUs: true, independentRetailerQuantity: "<50" },
            }}
            dispatchActionFalse={{
              type: "UPDATE_BRAND_APPLICATION",
              applicationPart: { independentRetailersInUs: false, independentRetailerQuantity: "none" },
            }}
            selectorFunction={retailerInUsSelector}
          />
          {rRetailerInUs && (
            <FormComboBoxInput<tApplicationBrandPath>
              titleStyle={styles.P08_01_InputHeader}
              wrapperStyle={styles.gLinkBankFormItemNoMarginZIndexSingle}
              myTitle="Approximately how many stores are you currently sold in? *"
              placeholder={"<50"}
              ComboOptions={aBrandStoreCount}
              dispatchAction={{
                type: "UPDATE_BRAND_APPLICATION",
                applicationPart: { independentRetailerQuantity: "none" },
              }}
              dispatchPath="applicationPart.independentRetailerQuantity"
              selectorFunction={retailerQuantitySelector}
            />
          )}
        </View>
      </View>

      {/* Second View */}
      <View style={styles.gTablePageContainerBottom}>
        <View style={styles.gFormContainer}>
          <Text style={styles.sectionTitle}>About Your Business</Text>
          <View style={styles.gFormRowNoMarginZIndex}>
            <FormComboBoxInput<tApplicationBrandPath>
              wrapperStyle={[styles.gLinkBankFormItemZIndex, styles.zIndexTwelve]}
              titleStyle={styles.P08_01_InputHeader}
              ComboOptions={aBrandCategories}
              dispatchAction={{
                type: "UPDATE_BRAND_APPLICATION",
                applicationPart: { category: undefined },
              }}
              dispatchPath="applicationPart.category"
              selectorFunction={brandCategorySelector}
              placeholder={"Category"}
              myTitle="What category best describes your Brands"
            />
            <FormComboBoxInput<tApplicationBrandPath>
              wrapperStyle={styles.gLinkBankFormItemNoMarginZIndex}
              titleStyle={styles.P08_01_InputHeader}
              ComboOptions={aPriceRange}
              dispatchAction={{
                type: "UPDATE_BRAND_APPLICATION",
                applicationPart: { price: "$" },
              }}
              dispatchPath="applicationPart.price"
              selectorFunction={pricePointSelector}
              placeholder={"Select Price Point"}
              myTitle="What retail price points are most of your products?"
            />
          </View>
          <TextFormInput<tApplicationBrandPath>
            wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
            titleStyle={styles.P08_01_InputHeader}
            myTitle="More Information"
            textInputProps={{
              multiline: true,
              numberOfLines: 12,
              style: styles.gLinkBankInputBox,
              placeholder: "Tell us a little more about you and your business.",
            }}
            dispatchAction={{
              type: "UPDATE_BRAND_APPLICATION",
              applicationPart: { note: "" },
            }}
            dispatchPath="applicationPart.note"
            selectorFunction={noteSelector}
          />
          <ValidatedComboBoxInput<tApplicationBrandPath>
            wrapperStyle={[styles.gLinkBankFormItemZIndex, { marginBottom: kStyleConst.kSpacing }]}
            titleViewStyle={styles.rowNoMargin}
            titleProps={{ style: styles.P08_01_InputHeader }}
            controller={isValid}
            ComboOptions={hearAboutUsOptions}
            rules={{ required: { message: "Required" } }}
            uniqueName="HearAbout"
            myTitle="How did you hear about us? *"
            placeholder="Select how did you hear about us"
            isShown={_isShown}
            setController={setValid}
            dispatchAction={{
              type: "UPDATE_BRAND_APPLICATION",
              applicationPart: { hearAboutUs: "social-media" },
            }}
            dispatchPath="applicationPart.hearAboutUs"
            selectorFunction={hearAboutUsSelector}
            searchable={rHearAboutUsDisabled}
          />
        </View>
      </View>
    </>
  );
};

export default BrandApplication;

const styles = StyleSheet.create({
  P08_01_InputHeader: {
    ...kFonts.poppinsRegular,
    color: kColors.darkGray,
    letterSpacing: 1.1,
  },
  P08_01_InputHeaderWrap: {
    flex: 1,
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 2 * kStyleConst.kSpacing,
    marginTop: 0,
    minWidth: kStyleConst.kApplyMinWidth,
  },
  gApplyTablePageContainer: {
    backgroundColor: kColors.white,
    borderRadius: 8,
    flexGrow: 1,
    marginTop: 7 * kStyleConst.kSpacing,
    zIndex: 2,
  },
  gFormContainer: {
    marginBottom: 12 * kStyleConst.kSpacing,
    marginHorizontal: 13 * kStyleConst.kSpacing,
    marginTop: 12 * kStyleConst.kSpacing,
    zIndex: 1,
  },
  gFormRowNoMargin: {
    alignItems: "flex-start",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    minWidth: kStyleConst.kApplyMinWidth,
  },
  gFormRowNoMarginSingle: {
    flexGrow: 1,
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    minWidth: kStyleConst.kApplyMinWidth,
  },
  gFormRowNoMarginValidatedSingle: {
    flexGrow: 1,
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    minWidth: kStyleConst.kApplyMinWidth,
  },
  gFormRowNoMarginZIndex: {
    alignItems: "flex-end",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    minWidth: kStyleConst.kApplyMinWidth,
    zIndex: 10,
  },
  gLinkBankFormItem: {
    flexGrow: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginRight: 8 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  gLinkBankFormItemBottomMargin: {
    flexGrow: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  gLinkBankFormItemNoMargin: {
    flexGrow: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  gLinkBankFormItemNoMarginZIndex: {
    flex: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    minWidth: kStyleConst.kApplyMinWidth,
    zIndex: 10,
  },
  gLinkBankFormItemNoMarginZIndexSingle: {
    flexGrow: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    minWidth: kStyleConst.kApplyMinWidth,
    zIndex: 10,
  },
  gLinkBankFormItemYesNo: {
    flexDirection: "row",
    flexGrow: 1,
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginBottom: 3 * kStyleConst.kSpacing,
    marginRight: 8 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  gLinkBankFormItemZIndex: {
    flex: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginRight: 8 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    minWidth: kStyleConst.kApplyMinWidth,
    zIndex: 100000,
  },
  gLinkBankInput: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    flex: 1,
    height: 36,
    marginLeft: 0,
    marginTop: 5,
    minWidth: kStyleConst.kApplyMinWidth,
    outlineColor: kColors.transparent,
    paddingLeft: kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  gLinkBankInputBox: {
    borderColor: kColors.brandPink,
    borderRadius: 2,
    borderWidth: 1,
    flex: 1,
    height: kStyleConst.kApplicationNotesBoxHeight,
    marginTop: 5,
    minWidth: kStyleConst.kApplyMinWidth,
    outlineColor: kColors.transparent,
    outlineWidth: 0,
    paddingLeft: 2 * kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  gLinkBankTripleInput: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    flex: 1,
    height: 36,
    marginTop: 5,
    minWidth: kStyleConst.kApplyMinWidth,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  gTablePageContainerBottom: {
    backgroundColor: kColors.white,
    borderRadius: 8,
    flexGrow: 1,
    marginVertical: 7 * kStyleConst.kSpacing,
    zIndex: 1,
  },
  rowNoMargin: {
    flexDirection: "row",
    marginTop: 0,
  },
  sectionTitle: {
    ...kFonts.ralewayLargeLight,
    alignSelf: "flex-start",
    marginBottom: 2 * kStyleConst.kSpacing,
  },
  zIndexTwelve: {
    zIndex: 12,
  },
});
